/**
 * MapaResponsaveis — o mapa etapa → membro do squad (SPEC A2 FR-11, FR-12; AG-06).
 *
 * Tabela com cada etapa aplicável (da conferência, FR-8) e o responsável com o selo da
 * origem (aprovado · proposto · especialista do catálogo · papel genérico). O seletor só
 * oferece MEMBROS do squad, com o selo Especialista/Genérico — especialistas primeiro.
 * Etapa `especialista_catalogo` mostra "⭐ melhor especialista do catálogo — proposto:
 * `<slug>` (entra no squad)"; etapa `papel_padrao` mostra "papel genérico — último caso:
 * `<slug>`". Ambas com Aceitar (inclui o membro e marca aprovado) e Escolher outro — no
 * Squad Designer elas já chegam aceitas ao carregar (auto-preenchimento), e o Aceitar só
 * aparece para proposta que surgir depois. Etapa coberta por `step_agents` mostra a nota "override do Catálogo: `<slug>`" —
 * o mapa aprovado prevalece.
 *
 * `somenteLeitura` (Gate de Squads): sem ações; o link "Editar no Squad Designer" vem de fora.
 */
import { useState } from 'react';
import { C } from '@/theme/palette';
import { TEXTO_DA_ORIGEM_RESP, ordenarPorEspecialidade, prefixoDoTipo, TITULO_DO_TIPO, tipoDoAgente } from './comum';
import { SeloTipo } from './SeloTipo';

const COR_DA_ORIGEM = { aprovado: 'cyber', proposto: 'warn', especialista_catalogo: 'warn', papel_padrao: 'warn' };

/**
 * Responsável efetivo de uma etapa: o aprovado no design local (se ainda for membro)
 * vence; senão vale a conferência (aprovado/proposta).
 */
export function responsavelEfetivo(etapa, { responsaveis = {}, responsaveisOrigem = {}, membros = [] }) {
  const ehMembro = (slug) => membros.some((m) => m.agent === slug);
  const local = responsaveis[etapa.etapa];
  const origemLocal = responsaveisOrigem[etapa.etapa] ?? 'aprovado';
  if (local && origemLocal === 'aprovado' && ehMembro(local)) return { slug: local, origem: 'aprovado' };
  if (etapa.origem === 'aprovado' && etapa.responsavel && ehMembro(etapa.responsavel) && !local) {
    return { slug: etapa.responsavel, origem: 'aprovado' };
  }
  if (etapa.proposta && etapa.origem && etapa.origem !== 'aprovado') {
    return { slug: etapa.proposta, origem: etapa.origem };
  }
  return { slug: null, origem: null };
}

function SeloOrigem({ origem }) {
  if (!origem) {
    return <span style={{ fontSize: 10.5, fontWeight: 600, color: C.hot }}>sem dono</span>;
  }
  const cor = C[COR_DA_ORIGEM[origem]] ?? C.neutralMuted;
  return (
    <span data-testid="origem-responsavel"
      style={{ fontSize: 10.5, fontWeight: 600, padding: '1px 7px', borderRadius: 5, color: cor,
        background: `${cor}18`, border: `1px solid ${cor}55`, whiteSpace: 'nowrap' }}>
      {TEXTO_DA_ORIGEM_RESP[origem] ?? origem}
    </span>
  );
}

function LinhaDaEtapa({ etapa, efetivo, membros, grupos, somenteLeitura, onEscolher, onAceitar }) {
  const [escolhendo, setEscolhendo] = useState(false);
  const rotulo = etapa.rotulo || etapa.etapa;
  const label = (slug) => membros.find((m) => m.agent === slug)?.label;
  const membroDe = (slug) => membros.find((m) => m.agent === slug);
  // Selo do responsável: tipo do membro; senão o tipo da proposta (`tipo_proposta`); senão a regra.
  const tipoDoEfetivo = (slug) => membroDe(slug)?.tipo
    ?? (slug && slug === etapa.proposta ? etapa.tipo_proposta : undefined)
    ?? tipoDoAgente(slug);
  // Com `grupos`, o seletor mostra cada squad com todos os seus membros (um agente em
  // dois squads aparece nos dois); sem eles, a lista única de membros.
  const gruposDoSeletor = grupos?.length
    ? grupos.map((g) => ({
      ...g,
      membros: ordenarPorEspecialidade(g.membros.map((m) => ({ ...m, ...membroDe(m.agent), label: m.label }))),
    }))
    : null;
  const opcoes = ordenarPorEspecialidade(membros);
  const opcao = (m, chave) => (
    <option key={chave} value={m.agent} data-tipo={tipoDoAgente(m)} title={TITULO_DO_TIPO[tipoDoAgente(m)]}>
      {prefixoDoTipo(m)}{m.agent}{m.label && m.label !== m.agent ? ` — ${m.label}` : ''}
    </option>
  );
  const pendente = efetivo.origem !== 'aprovado';
  const mostraSeletor = !somenteLeitura && (escolhendo || !efetivo.slug);

  return (
    <tr data-testid={`etapa-${etapa.etapa}`}>
      <td style={{ padding: '8px 10px', fontSize: 12.5, color: C.neutral, verticalAlign: 'top' }}>{rotulo}</td>
      <td style={{ padding: '8px 10px', fontSize: 12.5, verticalAlign: 'top' }}>
        {efetivo.origem === 'especialista_catalogo' ? (
          <div style={{ color: C.warn }}>
            ⭐ melhor especialista do catálogo — proposto: <code>{efetivo.slug}</code> (entra no squad){' '}
            <SeloTipo tipo={tipoDoEfetivo(efetivo.slug)} testId={`tipo-responsavel-${etapa.etapa}`} />
          </div>
        ) : efetivo.origem === 'papel_padrao' ? (
          <div style={{ color: C.warn }}>
            ⚠️ nenhum membro cobre esta etapa — papel genérico — último caso: <code>{efetivo.slug}</code> (entra no squad){' '}
            <SeloTipo tipo={tipoDoEfetivo(efetivo.slug)} testId={`tipo-responsavel-${etapa.etapa}`} />
          </div>
        ) : efetivo.slug ? (
          <div style={{ color: C.neutral }}>
            <code>{efetivo.slug}</code>
            {label(efetivo.slug) && label(efetivo.slug) !== efetivo.slug && (
              <span style={{ color: C.neutralMuted }}> · {label(efetivo.slug)}</span>
            )}{' '}
            <SeloTipo tipo={tipoDoEfetivo(efetivo.slug)} testId={`tipo-responsavel-${etapa.etapa}`} />
          </div>
        ) : (
          <div style={{ color: C.hot }}>Nenhum responsável — escolha um membro do squad.</div>
        )}
        {etapa.override_step_agents && (
          <div style={{ fontSize: 11, color: C.neutralMuted, marginTop: 3 }}>
            override do Catálogo: <code>{etapa.override_step_agents}</code>
            {efetivo.origem === 'aprovado' ? ' — o mapa aprovado prevalece' : ''}
          </div>
        )}
        {mostraSeletor && (
          <select aria-label={`Responsável por ${rotulo}`} value=""
            onChange={(e) => { if (e.target.value) { onEscolher(etapa.etapa, e.target.value); setEscolhendo(false); } }}
            style={{ marginTop: 6, width: '100%', background: C.black, color: C.neutral,
              border: `1px solid ${C.blackBorder}`, borderRadius: 6, padding: '5px 8px', fontSize: 12 }}>
            <option value="">Escolha um membro do squad…</option>
            {gruposDoSeletor
              ? gruposDoSeletor.map((g) => (
                <optgroup key={g.id} label={`${g.nome} (${g.membros.length})`}>
                  {g.membros.map((m, i) => opcao(m, `${g.id}-${m.agent}-${i}`))}
                </optgroup>
              ))
              : opcoes.map((m) => opcao(m, m.agent))}
          </select>
        )}
      </td>
      <td style={{ padding: '8px 10px', verticalAlign: 'top' }}><SeloOrigem origem={efetivo.origem} /></td>
      {!somenteLeitura && (
        <td style={{ padding: '8px 10px', verticalAlign: 'top', whiteSpace: 'nowrap' }}>
          {pendente && efetivo.slug && (
            <button type="button" onClick={() => onAceitar(etapa.etapa, efetivo.slug, efetivo.origem)}
              aria-label={`Aceitar ${efetivo.slug} para ${rotulo}`}
              style={{ fontSize: 11.5, padding: '3px 9px', borderRadius: 5, cursor: 'pointer', marginRight: 6,
                background: `${C.cyber}18`, color: C.cyber, border: `1px solid ${C.cyber}` }}>
              Aceitar
            </button>
          )}
          {efetivo.slug && (
            <button type="button" onClick={() => setEscolhendo((v) => !v)}
              aria-label={`Escolher outro responsável para ${rotulo}`}
              style={{ fontSize: 11.5, padding: '3px 9px', borderRadius: 5, cursor: 'pointer',
                background: 'transparent', color: C.neutralDim, border: `1px solid ${C.blackBorder}` }}>
              {escolhendo ? 'Cancelar' : 'Escolher outro'}
            </button>
          )}
        </td>
      )}
    </tr>
  );
}

/**
 * @param {{
 *   etapas: Array<{etapa, rotulo, aplicavel, responsavel, origem, proposta, tipo_proposta, override_step_agents}>,
 *   membros: Array<{agent, label, tipo?, prompt_status?}>,
 *   grupos?: Array<{id, nome, membros: Array<{agent, label}>}>,
 *   responsaveis?: Record<string,string>, responsaveisOrigem?: Record<string,string>,
 *   somenteLeitura?: boolean,
 *   onEscolher?: (etapa: string, slug: string) => void,
 *   onAceitar?: (etapa: string, slug: string, origem: string) => void,
 *   rodape?: React.ReactNode,
 * }} props
 */
export function MapaResponsaveis({
  etapas = [], membros = [], grupos = null, responsaveis = {}, responsaveisOrigem = {},
  somenteLeitura = false, onEscolher = () => {}, onAceitar = () => {}, rodape = null,
}) {
  const aplicaveis = (etapas || []).filter((e) => e && e.aplicavel !== false);
  const linhas = aplicaveis.map((e) => ({
    etapa: e,
    efetivo: somenteLeitura
      ? { slug: e.origem === 'aprovado' ? e.responsavel : e.proposta, origem: e.origem ?? null }
      : responsavelEfetivo(e, { responsaveis, responsaveisOrigem, membros }),
  }));
  const semDono = linhas.filter((l) => l.efetivo.origem !== 'aprovado').length;

  return (
    <div role="region" aria-label="Mapa etapa → membro">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, color: C.neutralMuted, letterSpacing: '0.06em', fontWeight: 600 }}>
          MAPA ETAPA → MEMBRO ({aplicaveis.length} etapas)
        </span>
        <span data-testid="etapas-sem-dono" style={{ fontSize: 11.5, color: semDono ? C.warn : C.cyber }}>
          {semDono ? `${semDono} etapa(s) sem responsável aprovado` : '✅ todas as etapas com responsável aprovado'}
        </span>
      </div>
      {aplicaveis.length === 0 ? (
        <div style={{ fontSize: 12.5, color: C.neutralDim }}>
          Salve a composição para conferir as etapas aplicáveis deste projeto.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }} aria-label="Responsável por etapa">
            <thead>
              <tr>
                {['Etapa', 'Responsável', 'Origem', ...(somenteLeitura ? [] : ['Ações'])].map((h) => (
                  <th key={h} style={{ textAlign: 'left', padding: '6px 10px', fontSize: 10.5, color: C.neutralMuted,
                    letterSpacing: '0.05em', textTransform: 'uppercase', borderBottom: `1px solid ${C.blackBorder}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {linhas.map(({ etapa, efetivo }) => (
                <LinhaDaEtapa key={etapa.etapa} etapa={etapa} efetivo={efetivo} membros={membros} grupos={grupos}
                  somenteLeitura={somenteLeitura} onEscolher={onEscolher} onAceitar={onAceitar} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {rodape}
    </div>
  );
}
